import { useEffect, useState, type FormEvent } from 'react'
import { campaignAlterationSummary, type RequestedCampaignAlteration } from '../campaignAlterations'
import { subtaskIsApprovedInAnalysis } from '../campaignReview'
import type { Task } from '../types'
import { Icon } from './Icon'

interface Props {
  task: Task
  userNames: Record<string, string>
  canRequestChanges: boolean
  canApproveAnalysis: boolean
  canWithdrawApprovals: boolean
  onApproveSubtasks: (subtaskIds: string[]) => void
  onWithdrawApproval: (subtaskId: string) => void
  onRequestChanges: (items: RequestedCampaignAlteration[]) => void
}

export function CampaignAlterations({ task, userNames, canRequestChanges, canApproveAnalysis, canWithdrawApprovals, onApproveSubtasks, onWithdrawApproval, onRequestChanges }: Props) {
  const [reasons, setReasons] = useState<Record<string, string>>({})
  const [approvalIds, setApprovalIds] = useState<string[]>([])
  const summary = campaignAlterationSummary(task.alterations)
  const selectedIds = Object.keys(reasons)
  const canSubmit = selectedIds.length > 0 && selectedIds.every(subtaskId => reasons[subtaskId].trim())
  const approvedCount = task.subtasks.filter(subtaskIsApprovedInAnalysis).length
  const showApproval = canApproveAnalysis || (canWithdrawApprovals && approvedCount > 0)

  useEffect(() => {
    setReasons({})
    setApprovalIds([])
  }, [task.id, task.status, task.alterations.length])

  function toggleSubtask(subtaskId: string, selected: boolean) {
    setReasons(current => {
      if (selected) return { ...current, [subtaskId]: '' }
      const next = { ...current }
      delete next[subtaskId]
      return next
    })
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canSubmit) return
    onRequestChanges(selectedIds.map(subtaskId => ({ subtaskId, reason: reasons[subtaskId].trim() })))
  }

  function submitApprovals(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!approvalIds.length) return
    onApproveSubtasks(approvalIds)
    setApprovalIds([])
  }

  function toggleApproval(subtaskId: string, selected: boolean) {
    setApprovalIds(current => selected ? [...new Set([...current, subtaskId])] : current.filter(id => id !== subtaskId))
  }

  return <section className="task-panel-section campaign-alterations" aria-labelledby="campaign-alterations-label">
    <div className="campaign-alterations-head">
      <div><h3 id="campaign-alterations-label">Análise e alterações</h3><p>Aprove as tarefas validadas ou sinalize individualmente o que precisa de ajuste.</p></div>
      <div className="alteration-counters" aria-label={`${summary.total} alterações registradas, ${summary.pending} pendentes`}>
        <span><strong>{summary.total}</strong> registradas</span>
        <span className={summary.pending ? 'pending' : ''}><strong>{summary.pending}</strong> pendentes</span>
      </div>
    </div>

    {showApproval && <form className="campaign-approval-form" onSubmit={submitApprovals}>
      <div className="campaign-approval-intro">
        <span className="approval-icon"><Icon name="check"/></span>
        <div><strong>Aprovar tarefas</strong><p>Selecione as tarefas validadas. A campanha será aprovada quando todas passarem pela análise.</p></div>
        <span className="campaign-approval-progress">{approvedCount}/{task.subtasks.length} aprovadas</span>
      </div>
      <div className="campaign-approval-list">{task.subtasks.map(subtask => {
        const approved = subtaskIsApprovedInAnalysis(subtask)
        const completed = subtask.status === 'Aprovada'
        const selectable = canApproveAnalysis && completed && !approved
        const selected = approvalIds.includes(subtask.id)
        return <div className={`campaign-approval-item ${selected ? 'selected' : ''} ${approved ? 'approved' : ''} ${!selectable && !approved ? 'unavailable' : ''}`} key={subtask.id}>
          <input type="checkbox" aria-label={`Selecionar ${subtask.title} para aprovação`} checked={approved || selected} disabled={approved || !selectable} onChange={event => toggleApproval(subtask.id, event.target.checked)}/>
          <span><strong>{subtask.title}</strong><small>{approved ? `Aprovada por ${userNames[subtask.analysisApprovedBy ?? ''] ?? subtask.analysisApprovedBy ?? 'revisor'}` : completed ? userNames[subtask.owner] ?? subtask.owner : 'Aguardando conclusão'}</small></span>
          <span className="campaign-approval-item-actions">
            <em>{approved ? 'Aprovada' : selected ? 'Selecionada' : completed ? 'Aguardando análise' : 'Não concluída'}</em>
            {approved && canWithdrawApprovals && <button type="button" onClick={() => onWithdrawApproval(subtask.id)}>Retirar aprovação</button>}
          </span>
        </div>
      })}</div>
      {canApproveAnalysis && <div className="campaign-approval-actions"><small>{approvalIds.length ? `${approvalIds.length} ${approvalIds.length === 1 ? 'tarefa selecionada' : 'tarefas selecionadas'}` : 'Selecione ao menos uma tarefa'}</small><button type="submit" disabled={!approvalIds.length}><Icon name="check"/>Aprovar selecionadas</button></div>}
    </form>}

    {canRequestChanges && <form className="campaign-rejection-form" onSubmit={submit}>
      <div className="campaign-rejection-intro"><span className="rejection-icon">!</span><div><strong>Reprovar análise</strong><p>Selecione cada tarefa que precisa voltar para alteração e informe a justificativa.</p></div></div>
      <div className="campaign-rejection-list">{task.subtasks.map(subtask => {
        const selected = Object.prototype.hasOwnProperty.call(reasons, subtask.id)
        return <div className={`campaign-rejection-item ${selected ? 'selected' : ''}`} key={subtask.id}>
          <label><input type="checkbox" checked={selected} onChange={event => toggleSubtask(subtask.id, event.target.checked)}/><span><strong>{subtask.title}</strong><small>{userNames[subtask.owner] ?? subtask.owner}</small></span></label>
          {selected && <textarea autoFocus rows={2} required value={reasons[subtask.id]} onChange={event => setReasons(current => ({ ...current, [subtask.id]: event.target.value }))} placeholder="Justificativa da reprovação" aria-label={`Justificativa da reprovação de ${subtask.title}`}/>}
        </div>
      })}</div>
      <div className="campaign-rejection-actions"><small>{selectedIds.length ? `${selectedIds.length} ${selectedIds.length === 1 ? 'tarefa selecionada' : 'tarefas selecionadas'}` : 'Selecione ao menos uma tarefa'}</small><button type="submit" disabled={!canSubmit}><Icon name="warning"/>Solicitar alterações</button></div>
    </form>}

    {task.alterations.length ? <div className="alteration-cycle-list">{[...task.alterations].reverse().map((alteration, reverseIndex) => {
      const cycleNumber = task.alterations.length - reverseIndex
      const pendingItems = alteration.items.filter(item => !item.resolvedAt).length
      return <article className="alteration-cycle" key={alteration.id}>
        <div className="alteration-cycle-head"><div><strong>Ciclo {cycleNumber}</strong><small>Solicitado por {userNames[alteration.requestedBy] ?? alteration.requestedBy} · {alteration.createdAt}</small></div><span className={pendingItems ? 'pending' : 'resolved'}>{pendingItems ? `${pendingItems} pendente${pendingItems === 1 ? '' : 's'}` : 'Concluído'}</span></div>
        <div className="alteration-cycle-items">{alteration.items.map(item => <div key={`${alteration.id}-${item.subtaskId}`}><span className={item.resolvedAt ? 'resolved' : 'pending'}>{item.resolvedAt ? '✓' : '!'}</span><div><strong>{item.subtaskTitle}</strong><p>{item.reason}</p><small>{item.resolvedAt ? 'Alteração concluída' : `Responsável: ${userNames[item.owner] ?? item.owner}`}</small></div></div>)}</div>
      </article>
    })}</div> : <div className="empty-campaign-alterations"><Icon name="check"/><div><strong>Nenhuma alteração registrada</strong><span>As tarefas reprovadas na análise serão contabilizadas e exibidas aqui.</span></div></div>}
  </section>
}
